import React from 'react';
import { Table } from '@hvcgroep/design-system';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { MeterReadsResponseModel } from '../../store/api-generated';

import './style.scss';

interface Props {
  meterReads: MeterReadsResponseModel[]
  className?: string
}

function formatDate(d: string) {
  const date = new Date(d);

  return new Intl.DateTimeFormat(document.documentElement.lang, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

const MeterReadsTable: React.FC<Props> = ({
  meterReads,
  className,
}) => {
  const { t } = useTranslation();
  const headers: (keyof Omit<MeterReadsResponseModel, 'quantity' | 'endDate' | 'unit' | 'beginReading' | 'significance' | 'dossierId' | 'referenceNumber'>)[] = ['startDate', 'endReading', 'readingMethod', 'meterNumber'];

  const classes = c('mo-meter-reads-table', className);

  return (
    <Table className={classes}>
      <Table.Head>
        <Table.Row>
          {headers.map((key) => {
            return (
              <Table.Header scope="col" key={`table-header-${key}`}>
                {t(`meterReadsTable.table.headers.${key}`)}
              </Table.Header>
            );
          })}
        </Table.Row>
      </Table.Head>

      <Table.Body>
        {meterReads.map((meterRead) => {
          const key = `${meterRead.referenceNumber || ''}-${meterRead.meterNumber || ''}-${meterRead.startDate || ''}`;
          return (
            <Table.Row key={key}>
              <Table.Data>
                {meterRead.startDate && formatDate(meterRead.startDate)}
              </Table.Data>
              <Table.Data className="mo-meter-reads-table__reads">
                {meterRead.endReading ? Number(meterRead.endReading).toLocaleString('nl-NL', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0,000'}
              </Table.Data>
              <Table.Data>
                <ReadingMethodSpecies species={meterRead.readingMethod} />
              </Table.Data>
              <Table.Data>
                {meterRead.meterNumber && meterRead.meterNumber}
              </Table.Data>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

function ReadingMethodSpecies({ species }: { species?: string }) {
  const { t } = useTranslation();
  return (
    <>
      {species ? t(`pages.usage.species.${species}` as 'pages.usage.species.SmartMeterReading' | 'pages.usage.species.PhysicalReading' | 'pages.usage.species.CustomerReading' | 'pages.usage.species.EstimatedReading') : null}
    </>
  );
}

export default MeterReadsTable;
